<template>
  <div
    v-if="current_template && (current_template.page || $route.name == 'WorkspaceItem')"
    id="design_container"
    class="h-screen !m-0 overflow-auto">
    <div ref="grid_layout" class="relative h-full overflow-auto w-6/6">
      <EmptyPage
        v-if="is_empty_layout"
        :current-template="current_template"
        :page-reference="pageReference"
        :page-name="current_template?.name"
        class="mt-52" />

      <grid-layout
        v-else
        :id="grid_layout_id"
        :layout="current_template.layout.filter((item) => !item.props?.hidden)"
        :col-num="72"
        :row-height="12"
        :is-draggable="editModeActive && canUserEditLayout($store.state.auth.user, current_template) && !editing_disabled"
        :is-resizable="true"
        :is-bounded="true"
        :prevent-collision="false"
        :vertical-compact="true"
        :restore-on-drag="false"
        :use-css-transforms="false"
        :responsive="false"
        @layout-updated="layoutUpdated">
        <grid-item
          v-for="item in current_template.layout.filter((item) => !item.props?.hidden)"
          :id="item.id"
          :key="item.id"
          :static="item.static"
          :x="item.x"
          :y="item.y"
          :w="item.w"
          :h="item.h"
          :min-w="item.minW"
          :min-h="item.minH"
          :max-w="item.maxW"
          :max-h="item.maxH"
          :min-x="item.minX"
          :max-x="item.maxX"
          :min-y="item.minY"
          :max-y="item.maxY"
          class="relative w-full h-full overflow-visible rounded-lg group no-touch bg-light-box"
          :preserve-aspect-ratio="item.preserveAspectRatio"
          @resized="itemResized"
          @moved="itemMoved">
          <template #default>
            <LayoutItemWrapper
              :prevent-components-update="prevent_component_update"
              :edit-mode="editModeActive"
              :item="item"
              :page-reference="pageReference"
              :current-template="current_template"
              :data-sources="data_sources" />
            <div
              v-if="
                editModeActive &&
                  !item.fixed &&
                  (item.props.component_type == 'rollup' ||
                    item.props.component_type == 'score' ||
                    item.props.component_type == 'graph' ||
                    item.props.component_type == 'forecast_table' ||
                    item.props.component_type == 'forecasting_metric' ||
                    item.props.component_type == 'record_table')
              "
              :class="[
                { 'right-6': !(!item.fixed || ['object_profile', 'company_profile', 'contact_profile'].includes(pageReference)) },
                { 'right-12': !item.fixed || ['object_profile', 'company_profile', 'contact_profile'].includes(pageReference) }
              ]"
              class="no-touch flex items-center w-5 h-5 justify-center hover:cursor-pointer duration-200 transition-all rounded-full shadow-md opacity-0 group-hover:opacity-100 text-white top-0 z-[999] absolute group-hover:bg-blue-400"
              @click.stop="replaceItem(item)">
              <ArrowLeftRight class="w-4 h-4 text-white" />
            </div>
            <div
              v-if="editModeActive"
              :class="[
                { 'right-0': !(!item.fixed || ['object_profile', 'company_profile', 'contact_profile'].includes(pageReference)) },
                { 'right-6': !item.fixed || ['object_profile', 'company_profile', 'contact_profile'].includes(pageReference) }
              ]"
              class="no-touch flex items-center w-5 h-5 justify-center hover:cursor-pointer duration-200 transition-all rounded-full shadow-md opacity-0 group-hover:opacity-100 text-white top-0 z-[999] absolute group-hover:bg-blue-400"
              @click.stop="editItem(item)">
              <Pencil color="white" class="w-4 h-4" />
            </div>
            <X
              v-if="editModeActive && (!item.fixed || ['object_profile', 'company_profile', 'contact_profile'].includes(pageReference))"
              class="no-touch w-5 h-5 hover:cursor-pointer duration-200 transition-all rounded-full shadow-md opacity-0 group-hover:opacity-100 text-white top-0 z-[999] absolute right-0 group-hover:bg-red-400 block"
              @click="deleteItem(item)" />
            <Expand
              v-if="editModeActive"
              class="no-touch vue-resizable-handle w-5 h-5 duration-200 transition-all hover:cursor-crosshair rounded-md shadow-md opacity-0 group-hover:opacity-100 group-hover:text-blue text-transparent bottom-0 z-[999] absolute right-0 block" />
          </template>
        </grid-item>
      </grid-layout>
    </div>
    <Modal
      v-model:is-open="delete_data_source_component_is_open"
      :default-success-msg="`Data source deleted`"
      max-width="max-w-[600px] mx-5">
      <template #form="{ close: closeModal }">
        <h3 class="text-lg font-medium leading-6 text-gray-900 dark:text-dark-body-text">
          {{ $t("Delete data source") }}
        </h3>
        <div class="max-w-xl mt-2 text-gray-500 text-md">
          <p>{{ $t("Do you want to also delete the data source for this page?") }}</p>
        </div>
        <div class="flex flex-row justify-end gap-2 mt-5 text-center">
          <button type="button" class="cancel-btn" @click="deleteDataSourceComponent(closeModal)">{{ $t("No") }}</button>
          <button type="button" class="danger-btn w-fit" @click="deleteDataSource(closeModal)">{{ $t("Yes") }}</button>
        </div>
      </template>
    </Modal>
  </div>
</template>

<script>
import Modal from "@/components/shared/Modal.vue"
import EmptyPage from "../add-new-component/EmptyPage.vue"
import { GridLayout, GridItem } from "rs-grid"
import { X, Pencil, ArrowLeftRight, Expand } from "lucide-vue-next"
import LayoutItemWrapper from "./LayoutItemWrapper.vue"
import { ALL_COMPONENTS } from "@/store/constants/all_components"
import dataService from "@/services/data.service"
import { utils } from "@/store/helpers"
import { v4 as uuid4 } from "uuid"
import getPageDefaultLayout from "@/components/shared/pages/default-layouts/index.js"
import { ALL_EMITTERS } from "@/store/constants/all_emitters"
import { MODULES } from "@/store/constants/references"

export default {
  components: {
    Modal,
    LayoutItemWrapper,
    Expand,
    GridLayout,
    GridItem,
    X,
    EmptyPage,
    Pencil,
    ArrowLeftRight
  },

  inject: [
    "getViewMetrics",
    "setPageLayout",
    "getPageLayout",
    "deletePageLayout",
    "isModuleActivated",
    "isModuleVisible",
    "canUserEditLayout",
    "updateLayouts"
  ],

  props: {
    pageReference: {
      type: String,
      required: true
    },
    // certain pages require object type (portfolio)
    objectType: {
      type: String,
      required: false
    },
    editModeActive: {
      type: Boolean,
      default: false
    },
    setEditMode: {
      type: Function,
      required: true
    }
  },
  emits: ["update:currentTemplate", "update:dataSources"],
  data() {
    return {
      delete_data_source_component_is_open: false,
      data_source_to_be_deleted: null,
      fetched_data_sources: [],
      grid_layout_id: 0,
      prevent_component_update: false,
      rollups_fetching: [],
      last_current_template: null,
      listeners: new Set(),
      data_sources: {},
      data_sources_copy: {},
      forecast_rollup_metrics: null,
      rollup_metrics: null,
      current_template: {
        layout: [],
        page: null,
        name: "",
        object_type: null,
        slug: "",
        id: null
      },
      last_templates: [],
      templates: [],
      profile_fetched: false,
      portfolio_fetched: false,
      forecasting_fetched: false,

      static_company_profile_pages: [
        { slug: "overview", reference_code: MODULES.COMPANY_PROFILE_OVERVIEW },
        { slug: "insights", reference_code: MODULES.COMPANY_PROFILE_INSIGHTS },
        { slug: "map", reference_code: MODULES.COMPANY_PROFILE_COMPANY_MAP },
        { slug: "actions", reference_code: MODULES.COMPANY_PROFILE_ACTIONS_INTERACTIONS },
        { slug: "checklists", reference_code: MODULES.COMPANY_PROFILE_CHECKLISTS }
      ],
      static_object_profile_pages: [
        { slug: "overview", reference_code: MODULES.COMPANY_PROFILE_OVERVIEW },
        { slug: "actions", reference_code: MODULES.COMPANY_PROFILE_INSIGHTS },
        { slug: "checklists", reference_code: MODULES.COMPANY_PROFILE_CHECKLISTS }
      ]
    }
  },

  computed: {
    is_empty_layout() {
      return this.current_template.layout.filter((l) => !l.props?.hidden).length === 0
    },
    editing_disabled() {
      let editing_disabled_calc = false
      if (this.$route.name == "WorkspaceItem" && this.$store.state.ui.workspace_user_access_scope == "read") {
        editing_disabled_calc = true
      }

      if (this.$route.name == "Dashboard" && this.$route.params.page) {
        const current_template = this.$store.state.portfolio.shared_layouts.find((l) => l.id == this.$route.params.page)
        if (!this.canUserEditLayout(this.$store.state.auth.user, current_template)) {
          editing_disabled_calc = true
        }
      }

      return editing_disabled_calc
    }
  },

  watch: {
    current_template: {
      deep: true,
      handler() {
        // All the components in the layout are watching the current_template
        this.$emit("update:currentTemplate", this.current_template)
      }
    },
    "$store.state.ui.currentWorkspace": {
      deep: true,
      handler() {
        this.$emitter.emit(ALL_EMITTERS.WORKSPACE_CHANGED)
      }
    },
    /*"$store.state.portfolio.pages_layout": {
      deep: true,
      handler(old_pages, new_pages) {
        if(!utils.isEqual(old_pages[this.pageReference], new_pages[this.pageReference])) {
          if (!this.editModeActive) {
          this.initLayout(true)
        }
        }
      }
    },*/
    editModeActive() {
      this.$emitter.emit(ALL_EMITTERS.IS_EDIT_MODE_ACIVE, this.editModeActive)
      this.$store.dispatch("ui/setEditModeActive", this.editModeActive)
      if (!this.editModeActive) {
        this.initLayout()
      }
    }
  },

  unmounted() {
    // this.$emitter.off("layoutUpdated")
    if (this.$store.state.ui.current_layout_owner_id) {
      this.$store.dispatch("ui/setUIValue", { current_layout_owner_id: null })
    }
  },
  deactivated() {
    this.unregister_listeners()
    this.unRegisterDataSourceEventsListener()
    // clear last opened layout id
    if (this.$store.state.ui.current_layout_owner_id) {
      this.$store.dispatch("ui/setUIValue", { current_layout_owner_id: null })
    }
  },
  activated() {
    this.register_listeners()
    if (
      this.$route.params.page ||
      (!this.$route.params.page && this.pageReference == "portfolio") ||
      (!this.$route.params.page && this.pageReference == "forecasting") ||
      (!this.$route.params.page && this.pageReference == "dashboard") ||
      (!this.$route.params.page && this.pageReference == "homepage") ||
      (!this.$route.params.page && this.pageReference == "customer_journeys")
    ) {
      this.initLayout()
    }

    if (
      !this.$route.params.page &&
      (this.pageReference == "company_profile" || this.pageReference == "object_profile" || this.pageReference == "contact_profile")
    ) {
      this.initLayout()
    }

    // NOTE: This part is critical is is used for sharing templates as data and template
    // Tag template Owner if the template is shared as a data and template (user see what the owner of the template sees)
    // only dashboard is shared as data and template
    if (["dashboard"].includes(this.current_template?.page)) {
      // eslint-disable-next-line vue/no-undef-properties
      if (
        // eslint-disable-next-line vue/no-undef-properties
        this.current_template.owner != this.$store.state.auth.user.id &&
        // eslint-disable-next-line vue/no-undef-properties
        this.current_template?.shared_with?.filter((u) => u.id == this.$store.state.auth.user.id && !u?.is_shared_as_template).length > 0
      ) {
        // eslint-disable-next-line vue/no-undef-properties
        this.$store.dispatch("ui/setUIValue", {
          current_layout_owner_id: this.current_template?.owner,
          page_reference: this.pageReference
        })
      }
    }
  },
  async created() {
    if (localStorage.getItem("layout_initiated") && this.pageReference !== "workspace") {
      return
    }

    if (
      this.$route.params.page ||
      (!this.$route.params.page && this.pageReference == "portfolio") ||
      (!this.$route.params.page && this.pageReference == "forecasting") ||
      (!this.$route.params.page && this.pageReference == "dashboard") ||
      (!this.$route.params.page && this.pageReference == "homepage")
    ) {
      this.initLayout()
    }

    if (
      !this.$route.params.page &&
      (this.pageReference == "company_profile" || this.pageReference == "object_profile" || this.pageReference == "contact_profile")
    ) {
      this.initLayout()
    }

    // NOTE: This part is critical is is used for sharing templates as data and template
    // Tag template Owner if the template is shared as a data and template (user see what the owner of the template sees)
    // only dashboard is shared as data and template
    if (["dashboard"].includes(this.current_template?.page)) {
      // eslint-disable-next-line vue/no-undef-properties
      if (
        this.current_template.owner != this.$store.state.auth.user.id &&
        this.current_template?.shared_with?.filter((u) => u.id == this.$store.state.auth.user.id && !u?.is_shared_as_template).length > 0
      ) {
        // eslint-disable-next-line vue/no-undef-properties
        this.$store.dispatch("ui/setUIValue", {
          current_layout_owner_id: this.current_template?.owner,
          page_reference: this.pageReference
        })
      }
    }
    localStorage.setItem("layout_initiated", true)
  },
  methods: {
    constructFilter(component_props) {
      let full_filter_calc = ""
      if (component_props.preservedMainFilter) {
        full_filter_calc = component_props.preservedMainFilter
      }
      if (component_props.preservedColumnsFilters) {
        full_filter_calc = full_filter_calc
          ? `${full_filter_calc} and ${component_props.preservedColumnsFilters}`
          : component_props.preservedColumnsFilters
      }

      if (component_props.preservedSelectedOwners?.length) {
        const owner_filter_string_calc = `( owner__id in [${component_props.preservedSelectedOwners.map((o) => `'${o}'`).join(", ")}] )`
        full_filter_calc = full_filter_calc ? `${full_filter_calc} and ${owner_filter_string_calc}` : owner_filter_string_calc
      }

      if (component_props.preservedSelectedCoOwners?.length) {
        let co_owner_filter_string_calc = `( co_owners__id in [${component_props.preservedSelectedCoOwners
          .map((o) => `'${o}'`)
          .join(", ")}] )`
        full_filter_calc = full_filter_calc ? `${full_filter_calc} and ${co_owner_filter_string_calc}` : co_owner_filter_string_calc
      }

      if (component_props.preservedSelectedOwners?.length) {
        let team_members_filter_string_calc = `( owner__id in [${component_props.preservedSelectedOwners
          .map((o) => `'${o}'`)
          .join(", ")}] )`
        full_filter_calc = full_filter_calc ? `${full_filter_calc} and ${team_members_filter_string_calc}` : team_members_filter_string_calc
      }

      if (component_props.dataSource == "this_record") {
        if (full_filter_calc) {
          full_filter_calc = full_filter_calc + ` and ( parent == '${this.$route.params?.cid}')`
        } else {
          full_filter_calc = `( parent == '${this.$route.params?.cid}')`
        }
      }

      if (utils.isUUID(component_props.dataSource)) {
        if (full_filter_calc) {
          full_filter_calc = full_filter_calc + ` and ( parent == '${component_props.dataSource}')`
        } else {
          full_filter_calc = `( parent == '${component_props.dataSource}')`
        }
      }

      return full_filter_calc
    },
    updateLayoutComponentProps(component_props) {
      if (!component_props?.id) {
        console.debug("Component id is required", component_props)
        return
      }
      const index = this.current_template.layout.findIndex((l) => l.id == component_props.id)
      if (index != -1) {
        // the id is not needed in the component props
        delete component_props.id
        const item = this.current_template.layout[index]
        this.current_template.layout.splice(index, 1, {
          ...item,
          props: { ...item.props, component_props: component_props }
        })
        this.updateLayout()
      }
    },

    register_listeners() {
      this.$emitter.on(ALL_EMITTERS.CURRENT_TEMPLATE_UPDATED, () => {
        this.initLayout(true)
      })
      this.$emitter.on(ALL_EMITTERS.SAVE_TEMPLATE, () => {
        this.updateLayout()
        this.setEditMode(false)
      })

      this.$emitter.on(ALL_EMITTERS.ROLLUP_TRUNCATE_BY_CHANGE, (data) => {
        this.refetchMetric(data.metric, { truncate_by: data.truncate_by })
      })

      this.$emitter.on(ALL_EMITTERS.UPDATE_LAYOUT_COMPONENT_PROPS, (component_props) => {
        this.updateLayoutComponentProps(component_props)
      })

      this.$emitter.on(ALL_EMITTERS.FORCE_INIT_LAYOUT, () => {
        this.initLayout(true)
      })
      this.$emitter.on(ALL_EMITTERS.WORKSPACE_CHANGED, () => {
        if (
          this.$store.state.ui.currentWorkspace &&
          this.$route.params?.page &&
          this.$route.params?.wid == this.$store.state.ui.currentWorkspace.short_id
        ) {
          this.initLayout(true)
        }
      })
      this.$emitter.on(ALL_EMITTERS.REFETCH_ROLLUP_METRIC, (rollup_metric) => {
        this.reCalculateRollupMetric(rollup_metric)
      })
      this.$emitter.on(ALL_EMITTERS.LAYOUT_UPDATED, ({ layout, silent = false }) => {
        if (!silent) {
          this.grid_layout_id += 1

          this.prevent_component_update = true
          setTimeout(() => {
            this.prevent_component_update = false
          }, 200)

          this.setEditMode(true)
        }

        // if we are on workspace
        if (this.pageReference == "workspace" || this.$route.name.includes("Workspace")) {
          if (layout && !utils.isEqual(layout, this.current_template)) {
            this.current_template = this.$utils.clone(layout)
            this.updateLayout(true)
          }
        } else if (layout.name == this.current_template.name && layout.page == this.current_template.page) {
          setTimeout(() => {
            this.current_template = this.$utils.clone(layout)
            this.updateLayout(true)
          }, 300)
        }
      })
      this.$emitter.on(ALL_EMITTERS.METRICS_ORDER_UPDATED_IN_TABLE, (layout) => {
        this.current_template = this.$utils.clone(layout)
      })
      this.$emitter.on(ALL_EMITTERS.REFRESH_ROLLUP_METRICS, async (data_source) => {
        await this.getRollupMetrics(this.data_sources[data_source])
      })
      this.$emitter.on(ALL_EMITTERS.REFRESH_STANDARD_METRICS, async ({ data_source, reference_name }) => {
        if (this.data_sources[data_source]) {
          this.data_sources[data_source].connected_metrics = [reference_name]
          await this.getRollupMetrics(this.data_sources[data_source])
        }
      })
      this.$emitter.on(ALL_EMITTERS.EDIT_LAYOUT, ({ page_name }) => {
        if (page_name == this.$route.params.page || this.$route.meta.page_reference == this.pageReference) {
          this.grid_layout_id += 1
          this.prevent_component_update = true
          setTimeout(() => {
            this.prevent_component_update = false
          }, 200)
          this.setEditMode(!this.editModeActive)
        } else {
          this.grid_layout_id += 1
          this.prevent_component_update = true
          setTimeout(() => {
            this.prevent_component_update = false
          }, 200)
          this.setEditMode(false)
        }
      })
      this.$emitter.on(ALL_EMITTERS.TOGGLE_EDIT_LAYOUT, () => {
        if (this.canUserEditLayout(this.$store.state.auth.user, this.current_template)) {
          this.grid_layout_id += 1
          this.prevent_component_update = true
          setTimeout(() => {
            this.prevent_component_update = false
          }, 200)
          this.setEditMode(!this.editModeActive)
        }
      })
      this.$emitter.on(ALL_EMITTERS.TOGGLE_ADD_REMOVE_COMPONENT, ({ selected_component = null, step = null }) => {
        if (this.canUserEditLayout(this.$store.state.auth.user, this.current_template)) {
          if (this.current_template.id) {
            this.$emitter.emit(ALL_EMITTERS.ADD_NEW_COMPONENT, {
              page_reference: this.pageReference,
              current_template: this.current_template,
              step: step,
              selected_component: selected_component
            })
          }
        }
      })
      this.$emitter.on(ALL_EMITTERS.DUPLICATE_PAGE, () => {
        let current_template_copy = this.$utils.clone(this.current_template)
        current_template_copy.name = utils.generateRandomKey("Page-", 3)
        delete current_template_copy["owner"]
        delete current_template_copy["shared_with"]
        delete current_template_copy["shared_at"]
        delete current_template_copy["cloned_at"]
        delete current_template_copy["cloned_with"]
        current_template_copy.id = uuid4()
        current_template_copy.is_static = false
        current_template_copy.slug = utils.slugify(current_template_copy.name)

        this.setPageLayout(current_template_copy)
        this.$router.push(`${current_template_copy.id}`)
      })
      this.$emitter.on(ALL_EMITTERS.ADD_GRAPH_TO_LAYOUT, (graph) => {
        this.addGraphItemDynamically({ ...graph, type: graph.type })
      })
      this.$emitter.on(ALL_EMITTERS.GRAPH_DELETED_IN_LAYOUT, (graph_id) => {
        const index = this.current_template.layout.findIndex((l) => l.props.component_props?.graph == graph_id)
        if (index != -1) {
          this.current_template.layout.splice(index, 1)
          this.$emitter.emit(ALL_EMITTERS.LAYOUT_UPDATED, {
            layout: this.current_template,
            page_name: this.$route.params.page
          })
          this.$emitter.emit(ALL_EMITTERS.LAYOUT_UPDATED_IN_ADD_COMPONENTES_MODAL, {
            layout: this.current_template,
            page_name: this.$route.params.page
          })
        }
      })
      //fires when text component form changes are submitted
      this.$emitter.on(ALL_EMITTERS.UPDATE_TEXT_INSTANCE, (data) => {
        const data_copy = this.$utils.clone(data)
        const index = this.current_template.layout?.findIndex((l) => l.id == data.id)
        if (index !== -1) {
          const item = this.current_template.layout[index]

          let component_props = { ...item.props?.component_props }
          //if component is connected to a record
          if (data_copy["connectTo"] && data_copy["connectTo"] !== "all_pages") {
            const page_identifier = data_copy["page_identifier"]
            delete data_copy.connectTo
            delete data_copy.page_identifier
            delete data_copy.required_filled
            delete data_copy.id
            delete data_copy.component_type

            Object.keys(data_copy).forEach((key) => {
              component_props[key][page_identifier] = data_copy[key]
            })
          } else {
            delete data_copy.connectTo
            delete data_copy.page_identifier
            delete data_copy.required_filled
            delete data_copy.id
            delete data_copy.component_type

            component_props = { ...data_copy }
          }

          this.current_template.layout.splice(index, 1, {
            ...item,
            props: { ...item.props, component_props: component_props }
          })
          this.updateLayout()
          this.$emit("update:currentTemplate", this.current_template)
        }
      })
      this.$emitter.on(ALL_EMITTERS.UPDATE_ASSETS_INSTANCE, (data) => {
        const data_copy = this.$utils.clone(data)
        const index = this.current_template.layout?.findIndex((l) => l.id == data.id)
        if (index !== -1) {
          const item = this.current_template.layout[index]
          let component_props = { ...item.props?.component_props }

          //if component is connected to a record
          if (data_copy["linked_to"] && data_copy["linked_to"] !== "this_page") {
            delete data_copy.id
            delete data_copy.component_type
            let attachments_list = []
            const attachments_to_create = []

            component_props.label = data_copy["label"]
            this.current_template.layout.splice(index, 1, {
              ...item,
              props: { ...item.props, component_props: component_props }
            })
            if (data_copy.attachments) {
              data_copy.attachments?.forEach((attachment) => {
                if (typeof attachment == "string" || attachment?.id) {
                  attachments_list.push(attachment.id ? attachment.id : attachment)
                } else {
                  attachments_to_create.push(attachment)
                }
              })
            }
            if (attachments_to_create.length) {
              dataService.addAttachment({ attachments: attachments_to_create }).then((result) => {
                attachments_list = attachments_list.concat(result.data.map((att) => att.id))
                dataService.updateRecord(data_copy.recordId, {
                  attachments: attachments_list
                })
              })
            } else {
              dataService.updateRecord(data_copy.recordId, {
                attachments: attachments_list
              })
            }
          } else {
            delete data_copy.id
            delete data_copy.component_type

            component_props = { ...data_copy }

            let attachments_list = []
            const attachments_to_create = []
            data_copy.attachments?.forEach((attachment) => {
              if (typeof attachment == "string" || attachment?.id) {
                attachments_list.push(attachment.id ? attachment.id : attachment)
              } else {
                attachments_to_create.push(attachment)
              }
            })

            if (attachments_to_create.length) {
              dataService.addAttachment({ attachments: attachments_to_create }).then((result) => {
                attachments_list = attachments_list.concat(result.data.map((att) => att.id))
                component_props.attachments = attachments_list
                this.current_template.layout.splice(index, 1, {
                  ...item,
                  props: { ...item.props, component_props: component_props }
                })
                this.updateLayout()
                this.$store.dispatch("getPortfolio")
              })
            } else {
              component_props.attachments = attachments_list
              this.current_template.layout.splice(index, 1, {
                ...item,
                props: { ...item.props, component_props: component_props }
              })
              this.updateLayout()
              this.$store.dispatch("getPortfolio")
            }
          }
        }
      }),
        this.$emitter.on(ALL_EMITTERS.UPDATE_NOTES_INSTANCE, (data) => {
          const data_copy = this.$utils.clone(data)

          const index = this.current_template.layout?.findIndex((l) => l.id == data.id)
          if (index !== -1) {
            const item = this.current_template.layout[index]
            let component_props = { ...item.props?.component_props }

            delete data_copy.page_identifier
            delete data_copy.required_filled
            delete data_copy.id
            delete data_copy.component_type

            component_props.notes = data_copy.notes
            this.current_template.layout.splice(index, 1, {
              ...item,
              props: { ...item.props, component_props: component_props }
            })
            this.updateLayout()
            this.$store.dispatch("getPortfolio")
          }
        })
    },
    unregister_listeners() {
      this.$emitter.off(ALL_EMITTERS.CURRENT_TEMPLATE_UPDATED)
      this.$emitter.off(ALL_EMITTERS.FORCE_INIT_LAYOUT)
      this.$emitter.off(ALL_EMITTERS.WORKSPACE_CHANGED)
      this.$emitter.off(ALL_EMITTERS.LAYOUT_UPDATED)
      this.$emitter.off(ALL_EMITTERS.REFRESH_ROLLUP_METRICS)
      this.$emitter.off(ALL_EMITTERS.EDIT_LAYOUT)
      this.$emitter.off(ALL_EMITTERS.TOGGLE_EDIT_LAYOUT)
      this.$emitter.off(ALL_EMITTERS.TOGGLE_ADD_REMOVE_COMPONENT)
      this.$emitter.off(ALL_EMITTERS.DUPLICATE_PAGE)
      this.$emitter.off(ALL_EMITTERS.ADD_GRAPH_TO_LAYOUT)
      this.$emitter.off(ALL_EMITTERS.GRAPH_DELETED_IN_LAYOUT)
      this.$emitter.off(ALL_EMITTERS.UPDATE_TEXT_INSTANCE)
      this.$emitter.off(ALL_EMITTERS.UPDATE_ASSETS_INSTANCE)
      this.$emitter.off(ALL_EMITTERS.UPDATE_NOTES_INSTANCE)
      this.$emitter.off(ALL_EMITTERS.REFETCH_ROLLUP_METRIC)
      this.$emitter.off(ALL_EMITTERS.ROLLUP_TRUNCATE_BY_CHANGE)
    },
    addGraphItemDynamically(data) {
      const x = (this.current_template.layout.filter((l) => !l.props?.hidden).length * 2) % 72
      let last_y = 0
      this.current_template.layout
        .filter((l) => !l.props?.hidden)
        .forEach((item) => {
          if (item.y + item.h > last_y) {
            last_y = item.y + item.h
          }
        })
      const y = last_y

      let w = 24
      let h = 24
      let minW = 18
      let minH = 18

      let preserveAspectRatio = false

      let props = {
        component_type: "graph",
        component_reference: `${this.pageReference}>${this.current_template.page}>${this.current_template.layout.length}`,
        hidden: false,
        component_props: {
          label: data?.name,
          objectType: data.type,
          graph: data.id,
          dataSource: data.dataSource ? data.dataSource : "all_records",
          textColor: data.textColor ? data.textColor : "black",
          backgroundColor: data.backgroundColor ? data.backgroundColor : "white"

          //selectedGroup: "f30e80ad-8162-419a-9aa1-9037fc1084a1"
        }
      }

      let item = {
        id: uuid4(),
        x: x,
        y: y,
        minW,
        minH,
        w: w,
        h: h,
        preserveAspectRatio,
        props: props
      }

      const current_template_copy = this.$utils.clone(this.current_template)

      current_template_copy.layout.push(item)

      this.$emitter.emit(ALL_EMITTERS.LAYOUT_UPDATED, { layout: current_template_copy })
    },

    initLayout(dont_set_edit_to_false) {
      /*
        Method that initializes the layout
        1. Get/Set the current template from based on the url or create a default one if is required (static page first access)
        2. Get all the templates for the page (each page can have multiple templates)
        3. Connect the data sources (rollups, forecast rollups, tables)
       */
      if (!dont_set_edit_to_false) {
        this.grid_layout_id += 1
        this.setEditMode(false)
      }
      // Get the current template from the url
      // the page can pe either the uuid or a slug
      let page_id_or_slug_from_url = this.$route.params?.page

      // for workspace page is the workspace short_id
      if (this.pageReference == "workspace" || this.$route.name.includes("Workspace")) {
        if (this.pageReference && this.$store.state.ui.currentWorkspace && page_id_or_slug_from_url) {
          if (!this.$utils.isEqual(this.$store.state.ui.currentWorkspace?.layout, this.last_templates)) {
            setTimeout(() => {
              this.templates = this.$utils.clone(this.$store.state.ui.currentWorkspace?.layout)
              this.current_template = this.templates?.find((l) => l.id == page_id_or_slug_from_url)
              this.last_templates = this.$utils.clone(this.$store.state.ui.currentWorkspace?.layout)
            }, 200)
          }
        }
      } else {
        // GET current template of create a new one or show not found page

        // Get all the templates for the page
        if (
          this.$store.state.portfolio.pages_layout[this.pageReference] ||
          this.$store.state.portfolio.shared_layouts.filter((l) => l.page == this.pageReference)
        ) {
          let existing_pages = []
          if (this.$store.state.portfolio.pages_layout[this.pageReference]) {
            existing_pages = this.$store.state.portfolio.pages_layout[this.pageReference]
          }
          if (existing_pages?.length) {
            this.templates = existing_pages?.concat(this.$store.state.portfolio.shared_layouts.filter((l) => l.page == this.pageReference))
          } else {
            this.templates = this.$store.state.portfolio.shared_layouts.filter((l) => l.page == this.pageReference)
          }
        }
        let layout_id = null // All layout ids are uuids
        let is_user_created_page = false
        // if there is not page_id_or_slug_from_url means this is a default page we get it or create it
        is_user_created_page = utils.isUUID(page_id_or_slug_from_url)

        if (is_user_created_page) {
          // the layout id is the page id in the url
          layout_id = this.templates.find((l) => l.id == page_id_or_slug_from_url)?.id
        } else {
          // find the layout id using the slug this only for
          layout_id = this.templates.find((l) => l.slug == page_id_or_slug_from_url)?.id
        }

        // we found the layout
        if (layout_id || layout_id === 0) {
          // all the layouts are uuids
          // the layout is found, set the current template to the layout
          this.current_template = JSON.parse(JSON.stringify(this.templates.find((l) => l.id == layout_id)))
        } else {
          // the layout is not found, create a default one and set the current template to it
          // Except for the dashboard page, we don't create a default template for it
          if (this.pageReference == "portfolio") {
            this.createAllPortfolioDefaultTemplates()
            this.portfolio_fetched = true
          } else if (this.pageReference == "forecasting") {
            this.createAllForecastingDefaultTemplates()
          } else if (this.pageReference != "dashboard") {
            this.createDefaultTemplate()
          }
        }
      }
      // if the layout is found we connect the data sources
      if (this.current_template) {
        this.connectLayoutDataSources()
      } else {
        // show not found page
        console.debug("not found page")
      }

      if (
        !this.profile_fetched &&
        (this.pageReference == "company_profile" || this.pageReference == "object_profile" || this.pageReference == "contact_profile")
      ) {
        this.createAllCompanyOrObjectProfileTemplates()
        this.profile_fetched = true
      }

      // if (!this.portfolio_fetched && this.pageReference == "portfolio") {
      //   this.createAllPortfolioDefaultTemplates()
      //   this.portfolio_fetched = true
      // }

      if (!this.forecasting_fetched && this.pageReference == "forecasting") {
        this.createAllForecastingDefaultTemplates()
        this.forecasting_fetched = true
      }
    },

    connectLayoutDataSources() {
      /* Method that loops through the layout components and group the required calls to fetch rollups,forecast rollups, and tables records */

      // keep a copy of the data sources
      this.data_sources_copy = this.$utils.clone(this.data_sources)
      this.current_template?.layout?.forEach((item) => {
        this.updateDataSources(item.props)
      })
      // locate data_source with same props (These are similar calls to the server) and register them so we can update the their initial records tables when the first one is fetched
      let same_data_sources = []
      Object.values(this.data_sources).forEach((data_source) => {
        if (
          same_data_sources.filter((ds) =>
            utils.isEqual(ds, data_source, true, [
              "connected_metrics",
              "component_reference",
              "initial_rollup_metrics",
              "same_initial_calls_data_sources"
            ])
          ).length == 0
        ) {
          same_data_sources.push(data_source)
        } else {
          // if the data_source is already in the same_data_sources array we add the component_reference to the same_initial_calls_data_sources array
          let index = same_data_sources.findIndex((ds) =>
            utils.isEqual(ds, data_source, true, [
              "connected_metrics",
              "component_reference",
              "initial_rollup_metrics",
              "same_initial_calls_data_sources"
            ])
          )
          data_source.same_initial_calls_data_sources.push(same_data_sources[index].component_reference)
        }
      })
      // we update the data sources, so other components in the same layout can use the updated data sources
      this.$emit("update:dataSources", this.data_sources)

      Object.values(this.data_sources).forEach((data_source) => {
        if (
          data_source.component_type == ALL_COMPONENTS.FORECASTING_TABLE ||
          data_source?.component_type == ALL_COMPONENTS.FORECASTING_METRIC
        ) {
          this.getForecastMetrics(data_source)
        } else {
          this.getRollupMetrics(data_source)
        }
      })
    },
    itemResized(item) {
      console.debug("itemResized", item)
    },
    itemMoved(item) {
      console.debug("itemMoved", item)
      // this.setEditMode(true)
      // this.updateLayout()
    },
    layoutUpdated() {
      // console.debug("layoutUpdated", new_layout)
    },
    updateLayout() {
      if (!this.$utils.isEqual(this.last_current_template, this.current_template)) {
        if (this.pageReference == "workspace") {
          const index = this.$store.state.ui.currentWorkspace.layout.findIndex((l) => l.id == this.$route.params.page)
          const layout_copy = this.$store.state.ui.currentWorkspace.layout

          if (
            this.current_template.id == this.$route.params.page &&
            this.$route.params?.wid == this.$store.state.ui.currentWorkspace.short_id
          ) {
            layout_copy.splice(index, 1, {
              ...layout_copy[index],
              layout: this.current_template.layout
            })
            this.$store.dispatch("data/updateWorkspace", {
              short_id: this.$route.params.wid,
              layout: layout_copy
            })
          }
        } else {
          this.setPageLayout(this.$utils.clone(this.current_template))
          this.last_current_template = this.$utils.clone(this.current_template)
          this.connectLayoutDataSources()
        }
      }
    },
    deleteItem(item) {
      if (item.props.component_type == "data_source") {
        this.data_source_to_be_deleted = item
        this.delete_data_source_component_is_open = true
      } else {
        const index = this.current_template.layout.findIndex((elm) => elm.type == item.type && elm.x == item.x && elm.y == item.y)
        this.current_template.layout.splice(index, 1)

        this.removeLinkedMetrics(item)
      }
    },
    deleteDataSource(closeModal) {
      const index = this.current_template.layout.findIndex(
        (elm) =>
          elm.type == this.data_source_to_be_deleted.type &&
          elm.x == this.data_source_to_be_deleted.x &&
          elm.y == this.data_source_to_be_deleted.y
      )
      this.current_template.layout.splice(index, 1)
      this.removeLinkedMetrics(this.data_source_to_be_deleted)
      closeModal()
      this.delete_data_source_component_is_open = false
    },
    deleteDataSourceComponent(closeModal) {
      const index = this.current_template.layout.findIndex(
        (elm) =>
          elm.type == this.data_source_to_be_deleted.type &&
          elm.x == this.data_source_to_be_deleted.x &&
          elm.y == this.data_source_to_be_deleted.y
      )
      this.current_template.layout.splice(index, 1)
      closeModal()
      this.delete_data_source_component_is_open = false
    },
    editItem(item) {
      this.$emitter.emit(ALL_EMITTERS.ADD_NEW_COMPONENT, {
        page_reference: this.pageReference,
        current_template: this.current_template
      })
      setTimeout(() => {
        this.$emitter.emit(ALL_EMITTERS.EDIT_INSTANCE, item)
      }, 400)
    },
    replaceItem(item) {
      this.$emitter.emit(ALL_EMITTERS.ADD_NEW_COMPONENT, {
        page_reference: this.pageReference,
        current_template: this.current_template
      })
      setTimeout(() => {
        this.$emitter.emit(ALL_EMITTERS.REPLACE_INSTANCE, item)
      }, 400)
    },

    removeLinkedMetrics(item) {
      const component_reference = item.props?.component_reference

      const layout_copy = this.$utils.clone(this.current_template.layout)
      this.current_template.layout.forEach((instance) => {
        if (instance.props?.component_type == "forecasting_metric" && instance.props?.component_props.dataSource == component_reference) {
          const index = layout_copy.findIndex((l) => l.id == instance.id)
          layout_copy.splice(index, 1)
        }
        if (instance.props?.component_type == "rollup" && instance.props?.component_props.dataSource == component_reference) {
          const index = layout_copy.findIndex((l) => l.id == instance.id)
          layout_copy.splice(index, 1, {
            ...instance,
            props: {
              ...instance.props,
              component_props: {
                ...instance.props?.component_props,
                dataSource: "all_records"
              }
            }
          })
        }
        if (instance.props?.component_type == "dynamic_source_selection") {
          const index = layout_copy.findIndex((l) => l.id == instance.id)
          layout_copy.splice(index, 1)

          layout_copy.map((layout) => {
            if (layout.props.component_type == "rollup" || layout.props.component_type == "score") {
              layout.props.component_props.dataSource = "all_records"
              layout.props.component_props.linkedRecord = null
            } else if (layout.props.component_type == "graph") {
              layout.props.component_props.dataSource = "all_records"
            } else if (layout.props.component_type == "notes") {
              layout.props.component_props.connectTo = "this_page"
              layout.props.component_props.linked_to = "this_page"
              layout.props.component_props.recordId = "this_page"
            } else if (layout.props.component_type == "record_table") {
              layout.props.component_props.dataSource = null
              layout.props.component_props.linkedRecord = null
              layout.props.component_props.preservedMainFilter = ""
              layout.props.component_props.preservedColumnsFilters = ""
            } else if (layout.props.component_props.initialFilter || layout.props.component_type == "action_table") {
              delete layout.props.component_props.initialFilter
            } else {
              layout.props.component_props.view_by = null
            }
          })
        }
      })

      this.current_template.layout = layout_copy
    },

    createDefaultTemplate() {
      let layout = []
      const page_id_or_slug_from_url = this.$route.params?.page
      let name = this.$utils.capitalize(this.pageReference)
      let slug = page_id_or_slug_from_url
      if (this.pageReference == "portfolio") {
        [layout, name, slug] = getPageDefaultLayout(this.pageReference, page_id_or_slug_from_url)
      } else if (this.pageReference == "homepage") {
        [layout, name, slug] = getPageDefaultLayout(this.pageReference, page_id_or_slug_from_url, this.isModuleActivated)
      } else if (this.pageReference == "prospecting") {
        [layout, name, slug] = getPageDefaultLayout(this.pageReference, page_id_or_slug_from_url, this.isModuleActivated)
      } else if (this.pageReference == "customer_journeys") {
        [layout, name, slug] = getPageDefaultLayout(this.pageReference, page_id_or_slug_from_url, this.isModuleActivated)
      } else if (
        this.pageReference == "company_profile" ||
        this.pageReference == "object_profile" ||
        this.pageReference == "contact_profile"
      ) {
        if (page_id_or_slug_from_url) {
          [layout, name, slug] = getPageDefaultLayout(this.pageReference, page_id_or_slug_from_url)
        } else {
          this.createAllPortfolioDefaultTemplates()
        }
      } else if (this.pageReference == "forecasting") {
        [layout, name, slug] = getPageDefaultLayout(this.pageReference, page_id_or_slug_from_url)
        const current_forecast = this.$store.state.data.current_forecast
        slug = page_id_or_slug_from_url
        name = current_forecast.name
      } else {
        layout = null
      }
      if (layout && this.pageReference !== "dashboard") {
        this.current_template = {
          id: this.pageReference == "forecasting" ? this.$store.state.data.current_forecast.id : uuid4(),
          page: this.pageReference,
          objectType: this.objectType,
          name: name,
          slug: slug,
          layout: layout,
          is_active: true,
          is_static: true,
          is_visible: true
        }

        this.templates.push(this.current_template)
        this.updateLayout()

        if (this.pageReference == "homepage") {
          this.$router.replace(`/homepage/` + this.current_template.slug)
        }
      }
    },
    createAllPortfolioDefaultTemplates() {
      // create all default objects missing pages for the portfolio
      const templates_calc = []
      const objects = this.$store.state.object_types.map((o) => o.type)
      let missing_objects_pages = objects.filter(
        (o) => !this.$store.state.portfolio.pages_layout["portfolio"].find((p) => p.objectType == o)
      )
      if (missing_objects_pages.length) {
        missing_objects_pages.forEach((object_type) => {
          let layout = []
          let name = null
          let slug = null
          ;[layout, name, slug] = getPageDefaultLayout("portfolio", object_type)
          const template = {
            id: uuid4(),
            page: "portfolio",
            objectType: object_type,
            name: name,
            slug: slug,
            layout: layout,
            is_active: true,
            is_visible: true,
            is_static: true,
            group_name: null
          }
          templates_calc.push(template)
        })
        this.updateLayouts(templates_calc, "portfolio")

        if (this.$route.params.page && templates_calc.find((t) => t.slug == this.$route.params.page)) {
          this.current_template = templates_calc.find((t) => t.slug == this.$route.params.page)
        } else {
          this.$router.replace(`/portfolio/${templates_calc[0].slug}`)
        }

        this.templates.push(this.current_template)
        this.updateLayout()
      }
    },

    createAllForecastingDefaultTemplates() {
      const existing_pages = this.$store.state.portfolio.pages_layout["forecasting"]
      const templates_calc = []

      const forecat_ids = this.$store.state.data.user_forecasts.map((f) => f.id)

      if (!existing_pages?.length || forecat_ids.length > existing_pages?.length) {
        forecat_ids.forEach((forecast_id) => {
          if (existing_pages && existing_pages.find((p) => p.id == forecast_id)) {
            templates_calc.push(existing_pages.find((p) => p.id == forecast_id))
          } else {
            let layout = []
            let name = null
            let slug = ""
            ;[layout, name, slug] = getPageDefaultLayout("forecasting", forecast_id)
            const template = {
              id: forecast_id,
              page: "forecasting",
              name: name,
              slug: slug,
              layout: layout,
              is_active: true,
              is_visible: true,
              is_static: true,
              group_name: null
            }
            templates_calc.push(template)
          }
        })
        this.updateLayouts(templates_calc, "forecasting")

        if (templates_calc.length) {
          this.$store.dispatch("data/selectForecast", this.$store.state.data.user_forecasts[0])
          this.$router.replace(`/revenue/forecast/${this.$store.state.data.user_forecasts[0].id}`)
        } else {
          this.current_template = templates_calc.find((t) => t.id == this.$route.params.page)
        }

        this.templates.push(this.current_template)
        this.updateLayout()
      }
    },

    createAllCompanyOrObjectProfileTemplates() {
      const static_pages = this.pageReference == "company_profile" ? this.static_company_profile_pages : this.static_object_profile_pages

      const existing_pages = this.$store.state.portfolio.pages_layout[this.pageReference] || []

      const existing_default_object_pages = existing_pages.filter((page) =>
        this.$store.state.object_types.map((o) => o.type).includes(page.slug)
      )

      const existing_custom_pages = existing_pages.filter(
        (page) => !static_pages.find((p) => p.slug == page.slug) && !this.$store.state.object_types.map((o) => o.type).includes(page.slug)
      )

      const templates_calc = []

      let object_pages
      if (this.pageReference == "company_profile") {
        object_pages = this.$store.state.object_types
          .map((o) => o.type)
          .filter((t) => t !== "company")
          .map((object_type) => {
            return { slug: object_type }
          })
      } else if (this.pageReference == "object_profile" && this.$route.params.object_type == "opportunity") {
        object_pages = this.$store.state.object_types
          .map((o) => o.type)
          .filter((t) => t !== "company" && t !== "opportunity")
          .map((object_type) => {
            return { slug: object_type }
          })
      } else {
        object_pages = []
      }

      const all_pages = static_pages.concat(object_pages)

      if (
        this.pageReference == "company_profile" ||
        (this.pageReference == "object_profile" && this.$route.params.object_type == "opportunity") ||
        !existing_pages.length
      ) {
        if (object_pages.length !== existing_default_object_pages.length) {
          all_pages.forEach((page_obj) => {
            if (existing_pages.find((p) => p.slug == page_obj.slug)) {
              templates_calc.push(existing_pages.find((p) => p.slug == page_obj.slug))
            } else {
              let layout = []
              let name = null
              let slug = ""
              ;[layout, name, slug] = getPageDefaultLayout(this.pageReference, page_obj.slug)
              const template = {
                id: uuid4(),
                page: this.pageReference,
                name: name,
                slug: slug,
                layout: layout,
                is_active: true,
                is_visible: true,
                is_static: true,
                group_name: null
              }
              templates_calc.push(template)
            }
          })
          existing_custom_pages.forEach((page) => {
            templates_calc.push(page)
          })

          this.updateLayouts(templates_calc, this.pageReference)
          this.current_template = templates_calc.find((t) => t.slug == this.$route.params.page)

          this.templates.push(this.current_template)
          this.updateLayout()
        }
      }
    },

    updateDataSources(item) {
      if (
        [
          ALL_COMPONENTS.OBJECT_TABLE,
          ALL_COMPONENTS.FORECASTING_TABLE,
          ALL_COMPONENTS.EXPANSION_LIST_TABLE,
          ALL_COMPONENTS.LEADS_TABLE,
          ALL_COMPONENTS.GROWTH_MATRIX,
          ALL_COMPONENTS.SUGGESTED_LIST
        ].includes(item?.component_type)
      ) {
        // check if data_sources has the item.component_reference keu

        if (!this.data_sources[item?.component_reference]) {
          let constructed_filter = ""

          const data_source_component_props = this.getLayoutComponentProps(item?.component_reference).component_props

          if (data_source_component_props) {
            constructed_filter = this.constructFilter(data_source_component_props)
          }

          this.data_sources[item?.component_reference] = {
            connected_metrics: [],
            initial_rollup_metrics: [],
            same_initial_calls_data_sources: [],
            selected_date_range: null,
            forecast_status: null,
            forecast_type: null,
            record_name: null,
            forecasted_value_gte: null,
            forecasted_value_lte: null,
            forecasted_value_min_gte: null,
            forecasted_value_min_lte: null,
            forecasted_value_max_gte: null,
            forecasted_value_max_lte: null,
            base_value_gte: null,
            base_value_lte: null,
            selected_group: null,
            selected_owners: null,
            selected_co_owners: null,
            selected_records: null,
            excluded_records: null,
            current_filter: constructed_filter ? constructed_filter : "",
            leads_groups_types: null,
            selected_forecast: null,
            selected_expansion_list: null,
            selected_growth_matrix: null,
            selected_suggested_list: null,
            component_reference: item?.component_reference,
            component_type: item?.component_type
          }
        }

        if (item?.component_type == ALL_COMPONENTS.EXPANSION_LIST_TABLE) {
          this.data_sources[item?.component_reference]["selected_expansion_list"] = item?.component_props.expansion_list
        } else if (item?.component_type == ALL_COMPONENTS.FORECASTING_TABLE) {
          this.data_sources[item?.component_reference]["selected_group"] = item?.component_props.view_by
          this.data_sources[item?.component_reference]["selected_forecast"] = item?.component_props.forecast_name
          this.data_sources[item?.component_reference]["selected_date_range"] = {
            from_date: this.$moment().startOf("quarter").format("YYYY-MM-DD"),
            to_date: this.$moment().endOf("quarter").format("YYYY-MM-DD")
          }
        } else if (item?.component_type == ALL_COMPONENTS.LEADS_TABLE) {
          // this.data_sources[item?.component_reference]["leads_groups_types"] = item?.component_props.groups_types
          this.data_sources[item?.component_reference]["leads_groups_types"] = item?.component_props.view_by
        } else if (item?.component_type == ALL_COMPONENTS.SUGGESTED_LIST) {
          this.data_sources[item?.component_reference]["selected_suggested_list"] = item?.component_props.suggested_list
        } else if (item?.component_type == ALL_COMPONENTS.GROWTH_MATRIX) {
          this.data_sources[item?.component_reference]["selected_growth_matrix"] = item?.component_props?.growth_matrix
        }
        this.registerDataSourceEventsListener(this.data_sources[item?.component_reference])
      } else if (
        item?.component_type == ALL_COMPONENTS.SCORE ||
        item?.component_type == ALL_COMPONENTS.ROLLUP_METRIC ||
        item?.component_type == ALL_COMPONENTS.FORECASTING_METRIC ||
        item?.component_type == ALL_COMPONENTS.STANDARD_METRICS
      ) {
        // check if data_sources has the item?.component_reference key
        let data_source_reference_name = item?.component_props.dataSource
        let data_source_is_record = false

        let constructed_filter = ""
        if (item?.component_props.dataSource == "another_record") {
          data_source_is_record = true
          data_source_reference_name = item?.component_props.linkedRecord
        } else {
          if (this.$utils.isUUID(item?.component_props.dataSource)) {
            const data_source_component_props = this.current_template.layout.find(
              (l) => l.id == item?.component_props.dataSource || l.props.component_reference == item?.component_props.dataSource
            )?.props?.component_props

            if (data_source_component_props) {
              constructed_filter = this.constructFilter(data_source_component_props)
            }
          } else if (item?.component_props.dataSource == "this_record" && this.$route.params?.cid) {
            data_source_is_record = true
            data_source_reference_name = this.$route.params.cid
          }
        }

        // include the data_source_reference_name in the data_sources[item?.component_reference].connected_metrics
        // if it is its data sources is not all records or empty

        const data_source_is_group = this.$store.state.data?.groups.find((g) => g.id == data_source_reference_name)

        if (data_source_reference_name !== "all_records" && this.$utils.isUUID(data_source_reference_name)) {
          this.$store.dispatch("data/getRecord", data_source_reference_name).then(() => {})
        }

        if (data_source_reference_name && item?.component_props.dataSource != "all_records") {
          let item_data_source = this.data_sources[data_source_reference_name]
          if (!item_data_source) {
            this.data_sources[data_source_reference_name] = {
              connected_metrics: [],
              initial_rollup_metrics: [],
              same_initial_calls_data_sources: [],
              selected_date_range: null,
              forecast_status: null,
              forecast_type: null,
              record_name: null,
              forecasted_value_gte: null,
              forecasted_value_lte: null,
              forecasted_value_min_gte: null,
              forecasted_value_min_lte: null,
              forecasted_value_max_gte: null,
              forecasted_value_max_lte: null,
              base_value_gte: null,
              base_value_lte: null,
              selected_group: data_source_is_group ? data_source_reference_name : null,
              selected_owners: null,
              selected_co_owners: null,
              selected_records: data_source_is_record ? [data_source_reference_name] : [],
              excluded_records: null,
              current_filter: constructed_filter ? constructed_filter : null,
              leads_groups_types: null,
              selected_forecast: null,
              selected_expansion_list: null,
              selected_growth_matrix: null,
              selected_suggested_list: null,
              component_reference: data_source_reference_name,
              unique: true,
              component_type: item?.component_type
            }
          } else {
            this.updateDataSources(this.getLayoutComponentProps(item?.component_props.dataSource))
          }
          item_data_source = this.data_sources[data_source_reference_name]

          // check if data_sources[item?.component_reference].connected_metrics has the item?.component_reference
          if (item?.component_type == ALL_COMPONENTS.FORECASTING_METRIC) {
            if (!item_data_source?.connected_metrics.includes(`${item?.component_props.metricName}`)) {
              item_data_source?.connected_metrics.push(`${item?.component_props.metricName}${item?.component_props.dataSource}`)
            }
          } else if (item?.component_type == ALL_COMPONENTS.STANDARD_METRICS) {
            let visible_standard_metrics_refs = item?.component_props.view_by.map((option) => {
              return this.$store.state.metrics.find((m) => m.reference_name == `company_${option}s_total_count_rs`).reference_name
            })
            visible_standard_metrics_refs.forEach((metric) => {
              if (!item_data_source?.connected_metrics.includes(metric)) {
                item_data_source?.connected_metrics.push(metric)
              }
            })
          } else {
            if (
              !item_data_source?.connected_metrics?.includes(
                `${
                  item?.component_props.metric
                    ? item?.component_props.metric
                    : this.$utils.getMetricById(item?.component_props.rollup || item?.component_props.score)?.reference_name
                }`
              )
            ) {
              item_data_source?.connected_metrics.push(
                `${
                  item?.component_props.metric
                    ? item?.component_props.metric
                    : this.$utils.getMetricById(item?.component_props.rollup || item?.component_props.score)?.reference_name
                }`
              )
            }
          }
        } else {
          // this means the rollup is for all records
          // we create a separate data source for it and add the rollup to it
          if (
            item?.component_type == ALL_COMPONENTS.SCORE ||
            item?.component_type == ALL_COMPONENTS.ROLLUP_METRIC ||
            ALL_COMPONENTS.STANDARD_METRICS
          ) {
            // eslint-disable-next-line vue/no-undef-properties
            if (!this.data_sources["all_records"]) {
              this.data_sources["all_records"] = {
                connected_metrics: [],
                initial_rollup_metrics: [],
                same_initial_calls_data_sources: [],
                selected_date_range: {},
                forecast_status: null,
                forecast_type: null,
                record_name: null,
                forecasted_value_gte: null,
                forecasted_value_lte: null,
                forecasted_value_min_gte: null,
                forecasted_value_min_lte: null,
                forecasted_value_max_gte: null,
                forecasted_value_max_lte: null,
                base_value_gte: null,
                base_value_lte: null,
                selected_group: null,
                selected_owners: null,
                selected_co_owners: null,
                selected_records: null,
                excluded_records: null,
                current_filter: null,
                leads_groups_types: null,
                selected_forecast: null,
                selected_expansion_list: null,
                selected_growth_matrix: null,
                selected_suggested_list: null,
                component_reference: "all_records",
                unique: true,
                component_type: item?.component_type
              }
            }
            if (item?.component_type == ALL_COMPONENTS.STANDARD_METRICS) {
              let visible_standard_metrics_refs = item?.component_props.view_by.map((option) => {
                return this.$store.state.metrics.find((m) => m.reference_name == `company_${option}s_total_count_rs`).reference_name
              })
              visible_standard_metrics_refs.forEach((metric) => {
                if (!this.data_sources["all_records"].connected_metrics.includes(metric)) {
                  this.data_sources["all_records"].connected_metrics.push(metric)
                }
              })
            } else {
              this.data_sources["all_records"].connected_metrics.push(
                `${
                  item?.component_props.metric
                    ? item?.component_props.metric
                    : this.$utils.getMetricById(item?.component_props.rollup || item?.component_props.score)?.reference_name
                }`
              )
            }
          }
        }
      }
    },
    registerDataSourceEventsListener(item) {
      // if the listener is already registered then return
      if (this.listeners.has(item?.component_reference)) {
        return
      } else {
        this.listeners.add(item?.component_reference)
      }
      this.$emitter.on(item?.component_reference, ({ event, data, trigger = null }) => {
        if (trigger) {
          //catch changed forecasted metric here
        }
        if (event == "selectedOwners") {
          this.getRollupMetricsIfFilterChanged(data, this.data_sources[item?.component_reference].selected_owners, item, () => {
            this.data_sources[item?.component_reference].selected_owners = this.$utils.clone(data)
          })
        } else if (event == "selectedCoOwners") {
          this.getRollupMetricsIfFilterChanged(data, this.data_sources[item?.component_reference].selected_co_owners, item, () => {
            this.data_sources[item?.component_reference].selected_co_owners = data
          })
        } else if (event == "currentFilter") {
          if (this.rollups_fetching.length) {
            setTimeout(() => {
              this.$emitter.emit(item?.component_reference, { event, data })
            }, 500)
          } else {
            this.getRollupMetricsIfFilterChanged(data, this.data_sources[item?.component_reference].current_filter, item, () => {
              this.data_sources[item?.component_reference].current_filter = data
            })
          }
        } else if (event == "selectedRecords") {
          this.getRollupMetricsIfFilterChanged(data, this.data_sources[item?.component_reference].selected_records, item, () => {
            this.data_sources[item?.component_reference].selected_records = data
          })
        } else if (event == "excludedRecords") {
          this.getRollupMetricsIfFilterChanged(data, this.data_sources[item?.component_reference].excluded_records, item, () => {
            this.data_sources[item?.component_reference].excluded_records = this.$utils.clone(data)
          })
        } else if (event == "selectedGroup") {
          this.getRollupMetricsIfFilterChanged(data, this.data_sources[item?.component_reference].selected_group, item, () => {
            this.data_sources[item?.component_reference].selected_group = data
          })
        } else if (event == "selectedExpansionList") {
          this.getRollupMetricsIfFilterChanged(data, this.data_sources[item?.component_reference].selected_expansion_list, item, () => {
            this.data_sources[item?.component_reference].selected_expansion_list = data
          })
        } else if (event == "selectedDateRange") {
          this.getRollupMetricsIfFilterChanged(data, this.data_sources[item?.component_reference].selected_date_range, item, () => {
            this.data_sources[item?.component_reference].selected_date_range = data
          })
        } else if (event == "truncateBy") {
          this.getRollupMetricsIfFilterChanged(data, this.data_sources[item?.component_reference].truncate_by, item, () => {
            this.data_sources[item?.component_reference].truncate_by = data
          })
        } else if (event == "searchRecordTerm") {
          this.getRollupMetricsIfFilterChanged(data, this.data_sources[item?.component_reference].record_name, item, () => {
            this.data_sources[item?.component_reference].record_name = data
          })
        } else if (event == "recordsChange") {
          if (this.data_sources[item?.component_reference]) {
            this.getForecastMetrics(this.data_sources[item?.component_reference])
          }
        } else if (event == "forecastStatuses") {
          this.getRollupMetricsIfFilterChanged(data, this.data_sources[item?.component_reference].forecast_status, item, () => {
            this.data_sources[item?.component_reference].forecast_status = data
          })
        } else if (event == "forecastTypes") {
          this.getRollupMetricsIfFilterChanged(data, this.data_sources[item?.component_reference].forecast_type, item, () => {
            this.data_sources[item?.component_reference].forecast_type = data
          })
        } else if (event == "forecastedBaseValue") {
          this.getRollupMetricsIfFilterChanged(
            data,
            {
              base_value_gte: this.data_sources[item?.component_reference].base_value_gte,
              base_value_lte: this.data_sources[item?.component_reference].base_value_lte
            },
            item,
            () => {
              this.data_sources[item?.component_reference].base_value_gte = data.base_value_gte
              this.data_sources[item?.component_reference].base_value_lte = data.base_value_lte
            }
          )
        } else if (event == "forecastedValue") {
          this.getRollupMetricsIfFilterChanged(
            data,
            {
              forecasted_value_gte: this.data_sources[item?.component_reference].forecasted_value_gte,
              forecasted_value_lte: this.data_sources[item?.component_reference].forecasted_value_lte
            },
            item,
            () => {
              this.data_sources[item?.component_reference].forecasted_value_gte = data.forecasted_value_gte
              this.data_sources[item?.component_reference].forecasted_value_lte = data.forecasted_value_lte
            }
          )
        } else if (event == "forecastedValueMin") {
          this.getRollupMetricsIfFilterChanged(
            data,
            {
              forecasted_value_min_gte: this.data_sources[item?.component_reference].forecasted_value_min_gte,
              forecasted_value_min_lte: this.data_sources[item?.component_reference].forecasted_value_min_lte
            },
            item,
            () => {
              this.data_sources[item?.component_reference].forecasted_value_min_gte = data.forecasted_value_min_gte
              this.data_sources[item?.component_reference].forecasted_value_min_lte = data.forecasted_value_min_lte
            }
          )
        } else if (event == "forecastedValueMax") {
          this.getRollupMetricsIfFilterChanged(
            data,
            {
              forecasted_value_max_gte: this.data_sources[item?.component_reference].forecasted_value_max_gte,
              forecasted_value_max_lte: this.data_sources[item?.component_reference].forecasted_value_max_lte
            },
            item,
            () => {
              this.data_sources[item?.component_reference].forecasted_value_max_gte = data.forecasted_value_max_gte
              this.data_sources[item?.component_reference].forecasted_value_max_lte = data.forecasted_value_max_lte
            }
          )
        }
      })
    },
    unRegisterDataSourceEventsListener() {
      // Object.keys(this.data_sources).forEach((ref) => {
      //   this.$emitter.off(ref)
      // })
    },
    async getRollupMetricsIfFilterChanged(newValue, oldValue, data_source, callback) {
      if (!this.$utils.isEqual(newValue, oldValue)) {
        if (
          data_source?.component_type == ALL_COMPONENTS.FORECASTING_TABLE ||
          data_source?.component_type == ALL_COMPONENTS.FORECASTING_METRIC
        ) {
          if (this.$utils.isNotEmpty(oldValue) && this.$utils.isEmpty(newValue && this.forecast_rollup_metrics?.length)) {
            callback()
            await this.getForecastMetrics(data_source)
          } else {
            callback()
            await this.getForecastMetrics(data_source)
          }
        } else {
          // rollup metrics
          if (this.$utils.isNotEmpty(oldValue) && this.$utils.isEmpty(this.rollup_metrics?.length)) {
            this.rollup_metrics = data_source?.initial_rollup_metrics
            this.rollup_metrics?.forEach((metric) => {
              this.$emitter.emit(ALL_EMITTERS.REFRESH_ROLLUP_METRIC, {
                metric: metric,
                data_source: data_source?.component_reference
              })
            })
          } else {
            callback()
            await this.getRollupMetrics(data_source)
          }
        }
      }
    },
    getLayoutComponentProps(component_reference) {
      return this.current_template.layout.find((l) => l.props?.component_reference == component_reference)?.props
    },
    async getRollupMetrics(data_source) {
      if (data_source?.connected_metrics.map((ref) => this.$utils.getMetricByReferenceName(ref)?.id).filter((x) => x).length == 0) {
        return
      }
      this.fetched_data_sources.push(data_source)
      this.rollups_fetching.push(data_source)

      await dataService
        .getRollupMetrics({
          page_reference: this.pageReference,
          target_metrics: data_source?.connected_metrics.map((ref) => this.$utils.getMetricByReferenceName(ref)?.id).filter((x) => x),
          records: data_source?.selected_records,
          excluded_records: this.$utils.clone(data_source?.excluded_records),
          selected_forecast: data_source?.selected_forecast,
          selected_expansion_list: data_source?.selected_expansion_list,
          selected_growth_matrix: data_source?.selected_growth_matrix,
          selected_suggested_list: data_source?.selected_suggested_list,
          groups: data_source?.selected_group
            ? data_source?.selected_group.id
              ? [data_source?.selected_group.id]
              : [data_source?.selected_group]
            : [],
          leads_groups_types: data_source?.leads_groups_types,
          record_name: data_source?.record_name,
          owners: data_source?.selected_owners,
          co_owners: data_source?.selected_co_owners,
          from_date: data_source?.selected_date_range?.from_date,
          to_date: data_source?.selected_date_range?.to_date,
          records_filter: data_source?.current_filter,
          truncate_by: data_source?.truncate_by ? data_source?.truncate_by : "day"
        })
        .then((data) => {
          this.rollup_metrics = data.data
          if (utils.isEqual(this.data_sources_copy[data_source?.component_reference], data_source)) {
            // cache the initial rollup metrics values
            if (data_source) {
              data_source.initial_rollup_metrics = data.data
            }
          }
          if (this.rollup_metrics?.length) {
            this.rollup_metrics?.forEach((metric) => {
              this.$emitter.emit(ALL_EMITTERS.REFRESH_ROLLUP_METRIC, {
                metric: metric,
                data_source: data_source?.component_reference
              })
            })
          }

          // setTimeout(() => {
          const rollup_index_in_rollups_fetching_index = this.rollups_fetching.findIndex((r) => this.$utils.isEqual(r, data_source))
          if (rollup_index_in_rollups_fetching_index !== -1) {
            this.rollups_fetching.splice(rollup_index_in_rollups_fetching_index, 1)
          }
          // }, 2000)
        })
    },

    async reCalculateRollupMetric(metric) {
      const data_sources_cleaned = {}
      Object.keys(this.data_sources).forEach((data_source) => {
        this.data_sources[data_source].connected_metrics.forEach((m) => {
          //the same metric or it is a computed metric that uses the 'metric' or metric thats a building block of a rollup metric
          if (
            this.$utils.getMetricByReferenceName(m).included_metrics.includes(metric.id) ||
            metric.related_metrics.includes(this.$utils.getMetricByReferenceName(m).id) ||
            m == metric.reference_name
          ) {
            if (data_sources_cleaned[data_source]) {
              //avoid sending it twice
              if (!data_sources_cleaned[data_source]["connected_metrics"].includes(m)) {
                data_sources_cleaned[data_source]["connected_metrics"].push(m)
              }
            } else {
              data_sources_cleaned[data_source] = {
                ...this.data_sources[data_source],
                connected_metrics: [m]
              }
            }
          }
        })

        if (data_source == metric.id) {
          data_sources_cleaned[data_source] = this.data_sources[data_source]
        }
      })
      Object.keys(data_sources_cleaned).forEach((cleaned_data_source) => {
        this.getRollupMetrics(data_sources_cleaned[cleaned_data_source], true)
      })
    },

    async refetchMetric(metric, data) {
      const data_sources_cleaned = {}
      Object.keys(this.data_sources).forEach((data_source) => {
        this.data_sources[data_source].connected_metrics.forEach((m) => {
          //the same metric or it is a computed metric that uses the 'metric'
          if (m == metric.reference_name) {
            if (data_sources_cleaned[data_source]) {
              data_sources_cleaned[data_source].truncate_by = data.truncate_by
              //avoid sending it twice
              if (!data_sources_cleaned[data_source]["connected_metrics"].includes(m)) {
                data_sources_cleaned[data_source]["connected_metrics"].push(m)
              }
            } else {
              data_sources_cleaned[data_source] = {
                ...this.data_sources[data_source],
                connected_metrics: [m],
                truncate_by: data.truncate_by
              }
            }
          }
        })

        if (data_source == metric.id) {
          data_sources_cleaned[data_source] = this.data_sources[data_source]
        }
      })

      Object.keys(data_sources_cleaned).forEach((cleaned_data_source) => {
        this.getRollupMetrics(data_sources_cleaned[cleaned_data_source], true)
      })
    },

    async getForecastMetrics(data_source) {
      let forecast_id = this.getLayoutComponentProps(data_source?.component_reference).component_props.forecast_name
      // if there is a none solid rollup metrics
      let computed_target_metrics = data_source?.connected_metrics
        .map((ref) => this.$utils.getMetricByReferenceName(ref)?.id)
        .filter((x) => x)
      if (computed_target_metrics.length > 0) {
        this.getRollupMetrics(data_source)
      }

      await dataService
        .getForecastsStats(forecast_id, {
          page_reference: this.pageReference,
          target_metrics: [],
          records: data_source?.selected_records,
          excluded_records: this.$utils.clone(data_source?.excluded_records),
          selected_forecast: data_source?.selected_forecast,
          group: data_source?.selected_group,
          forecast_status: data_source?.forecast_status,
          forecast_type: data_source?.forecast_type,
          forecasted_value_gte: data_source?.forecasted_value_gte,
          forecasted_value_lte: data_source?.forecasted_value_lte,
          forecasted_value_min_gte: data_source?.forecasted_value_min_gte,
          forecasted_value_min_lte: data_source?.forecasted_value_min_lte,
          forecasted_value_max_gte: data_source?.forecasted_value_max_gte,
          forecasted_value_max_lte: data_source?.forecasted_value_max_lte,
          base_value_gte: data_source?.base_value_gte,
          base_value_lte: data_source?.base_value_lte,
          selected_expansion_list: data_source?.selected_expansion_list,
          selected_growth_matrix: data_source?.selected_growth_matrix,
          selected_suggested_list: data_source?.selected_suggested_list,
          // groups: data_source?.selected_group ? [data_source?.selected_group.id] : [],
          leads_groups_types: data_source?.leads_groups_types,
          co_owners: data_source?.selected_co_owners,
          owners: data_source?.selected_owners,
          // owner_id: data_source?.selected_owner_id,
          record_name: data_source?.record_name,
          from_date: data_source?.selected_date_range?.from_date,
          to_date: data_source?.selected_date_range?.to_date,
          records_filter: data_source?.current_filter
        })
        .then((data) => {
          this.forecast_rollup_metrics = data.data
          if (utils.isEqual(this.data_sources_copy[data_source?.component_reference], data_source)) {
            // cache the initial rollup metrics values
            data_source.initial_rollup_metrics = data.data
          }
          if (this.forecast_rollup_metrics?.length) {
            this.$emitter.emit(ALL_EMITTERS.REFRESH_FORECAST_TABLE_METRICS, {
              metrics: this.forecast_rollup_metrics,
              data_source: data_source?.component_reference
            })
            this.forecast_rollup_metrics.forEach((forecast_metric) => {
              this.$emitter.emit(ALL_EMITTERS.REFRESH_FORECAST_METRIC, {
                metric: forecast_metric,
                data_source: data_source?.component_reference
              })
            })
          }
        })
    }
  }
}
</script>
